import {
  CHART_NAME_MAX_LENGTH,
  DEFAULT_CHART_SETTINGS,
  LINE_STYLES,
  MAX_ACTIVE_SERIES,
  MAX_CHARTS,
  TREND_SCALES,
} from "../constants";
import type { ChartSettings, LineStyleKey, SeriesKey, Chart, TrendRange, TrendScale, TrendVariant } from "../types";
import { isTrendVariant } from "./dashboardSeries";
import { activeSeriesIds, seriesKey } from "./trendOps";

/**
 * A saved trend's `config`: the whole Trends page as the user saved it (see
 * the `trends` table migration). Series keep their order in each chart, and
 * charts keep theirs on the page. A series' variant and line style belong to
 * it across the whole page, so they're kept once, in `series`, and only for
 * what the user set: with no `variant` it shows its default, with no
 * `line_style` it gets its automatic one. Hiding a series is per chart.
 * `chart_settings` holds only the settings that differ from the default look,
 * so a change to the defaults reaches saved trends too.
 *
 * Each chart keeps its `id` for as long as it exists, so something outside
 * the trend (a dashboard tile, later) can point at one chart. `next_chart_id`
 * numbers the next new chart, so a deleted chart's id is never given to
 * another. `expanded_chart_ids` are the charts shown enlarged, in the order
 * they were enlarged.
 */
export interface TrendConfig {
  version: 1;
  scale: TrendScale;
  range: SavedRange;
  chart_settings?: Partial<ChartSettings>;
  /** Each series' variant and line style, by display id; only series the charts hold. */
  series?: Record<string, SavedSeriesStyle>;
  charts: SavedChart[];
  next_chart_id: number;
  expanded_chart_ids?: string[];
}

export interface SavedChart {
  /** Unique within its trend, and never reused. */
  id: string;
  /** The name the user gave the chart; without one it's named automatically. */
  name?: string;
  /** Set when this chart uses a different scale than the page. */
  scale: TrendScale | null;
  /** Set when this chart shows a different date range than the page. */
  range?: SavedRange;
  series: SavedSeries[];
}

export interface SavedSeries {
  display_id: string;
  hidden?: boolean;
}

export interface SavedSeriesStyle {
  variant?: TrendVariant;
  line_style?: LineStyleKey;
}

/** An object so custom date ranges won't need a new format. */
export type SavedRange = { kind: "years"; years: 1 | 3 | 5 } | { kind: "max" };

/** The parts of the Trends page a saved trend holds. */
export interface TrendPage {
  charts: Chart[];
  /** The number the next new chart's id (`t<n>`) takes. */
  nextId: number;
  chosenVariant: Record<string, TrendVariant>;
  startingVariant: Record<string, TrendVariant>;
  scale: TrendScale;
  range: TrendRange;
  lineStyle: Record<string, LineStyleKey>;
  chartSettings: ChartSettings;
  hidden: Record<SeriesKey, true>;
  /** The charts shown enlarged, in the order they were enlarged. */
  expandedChartIds: string[];
}

export const TREND_NAME_MAX_LENGTH = 120;
const DISPLAY_ID_MAX_LENGTH = 100;
const CHART_ID = /^[A-Za-z0-9_-]{1,40}$/;
/** Ids too big to be a chart counter, which can't sensibly reach this. */
const MAX_CHART_NUMBER = 1_000_000;

/** The number in a `t<n>` chart id, or 0 for any other id. */
const chartNumber = (id: string) => {
  const match = /^t(\d+)$/.exec(id);
  return match ? Number(match[1]) : 0;
};

/** The next chart number: past every `t<n>` id in use, and never below `next`. */
const nextChartNumber = (ids: string[], next = 1) => Math.max(next, ...ids.map((id) => chartNumber(id) + 1));

const toSavedRange = (range: TrendRange): SavedRange =>
  range === "max" ? { kind: "max" } : { kind: "years", years: Number(range) as 1 | 3 | 5 };

const fromSavedRange = (range: SavedRange): TrendRange => (range.kind === "max" ? "max" : `${range.years}`);

const sameSetting = <K extends keyof ChartSettings>(key: K, value: ChartSettings[K]) =>
  value === DEFAULT_CHART_SETTINGS[key] || JSON.stringify(value) === JSON.stringify(DEFAULT_CHART_SETTINGS[key]);

/** The page as a saved trend's `config`. */
export function toTrendConfig(page: TrendPage): TrendConfig {
  const changedSettings = (Object.keys(DEFAULT_CHART_SETTINGS) as (keyof ChartSettings)[]).filter(
    (key) => !sameSetting(key, page.chartSettings[key]),
  );
  const styles = Array.from(new Set(page.charts.flatMap((t) => t.seriesIds))).flatMap((displayId) => {
    const variant = page.chosenVariant[displayId] ?? page.startingVariant[displayId];
    const lineStyle = page.lineStyle[displayId];
    if (!variant && !lineStyle) return [];
    return [[displayId, { ...(variant ? { variant } : {}), ...(lineStyle ? { line_style: lineStyle } : {}) }] as const];
  });
  return {
    version: 1,
    scale: page.scale,
    range: toSavedRange(page.range),
    ...(changedSettings.length > 0
      ? { chart_settings: Object.fromEntries(changedSettings.map((key) => [key, page.chartSettings[key]])) }
      : {}),
    ...(styles.length > 0 ? { series: Object.fromEntries(styles) } : {}),
    charts: page.charts.map((chart) => ({
      id: chart.id,
      ...(chart.name ? { name: chart.name } : {}),
      scale: chart.scaleOverride ?? null,
      ...(chart.rangeOverride && chart.rangeOverride !== page.range
        ? { range: toSavedRange(chart.rangeOverride) }
        : {}),
      series: chart.seriesIds.map((displayId) => ({
        display_id: displayId,
        ...(page.hidden[seriesKey(chart.id, displayId)] ? { hidden: true } : {}),
      })),
    })),
    next_chart_id: nextChartNumber(
      page.charts.map((t) => t.id),
      page.nextId,
    ),
    ...(() => {
      const expanded = page.expandedChartIds.filter((id) => page.charts.some((t) => t.id === id));
      return expanded.length > 0 ? { expanded_chart_ids: expanded } : {};
    })(),
  };
}

/** The page a saved trend's `config` opens, each chart under its saved id. */
export function fromTrendConfig(config: TrendConfig): Omit<TrendPage, "startingVariant"> {
  const chosenVariant: Record<string, TrendVariant> = {};
  const lineStyle: Record<string, LineStyleKey> = {};
  for (const [displayId, style] of Object.entries(config.series ?? {})) {
    if (style.variant) chosenVariant[displayId] = style.variant;
    if (style.line_style) lineStyle[displayId] = style.line_style;
  }
  const hidden: Record<SeriesKey, true> = {};
  const pageRange = fromSavedRange(config.range);
  const charts = config.charts.map((chart): Chart => {
    const id = chart.id;
    const range = chart.range ? fromSavedRange(chart.range) : pageRange;
    for (const s of chart.series) {
      if (s.hidden) hidden[seriesKey(id, s.display_id)] = true;
    }
    return {
      id,
      seriesIds: chart.series.map((s) => s.display_id),
      ...(chart.name ? { name: chart.name } : {}),
      ...(chart.scale && chart.scale !== config.scale ? { scaleOverride: chart.scale } : {}),
      ...(range !== pageRange ? { rangeOverride: range } : {}),
    };
  });
  return {
    charts,
    nextId: nextChartNumber(
      charts.map((t) => t.id),
      config.next_chart_id,
    ),
    chosenVariant,
    lineStyle,
    hidden,
    scale: config.scale,
    range: pageRange,
    expandedChartIds: config.expanded_chart_ids ?? [],
    chartSettings: {
      ...DEFAULT_CHART_SETTINGS,
      bandColors: [...DEFAULT_CHART_SETTINGS.bandColors],
      ...config.chart_settings,
    },
  };
}

// ---------------------------------------------------------------------------
// Validation: what the API accepts and what a loaded row must be.
// ---------------------------------------------------------------------------

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isTrendScale = (value: unknown): value is TrendScale => TREND_SCALES.some((s) => s.value === value);

const isLineStyle = (value: unknown): value is LineStyleKey => LINE_STYLES.some((s) => s.key === value);

const isNumberIn = (value: unknown, min: number, max: number): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;

const isHexColor = (value: unknown): value is string => typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);

/** Each chart setting's check, loose enough for any value the settings popover can set. */
const SETTING_CHECKS: { [K in keyof ChartSettings]: (value: unknown) => boolean } = {
  lineWidth: (v) => isNumberIn(v, 0.5, 10),
  labelSize: (v) => isNumberIn(v, 6, 32),
  gridOpacity: (v) => isNumberIn(v, 0, 1),
  showDots: (v) => typeof v === "boolean",
  showZoomSlider: (v) => typeof v === "boolean",
  bandOpacity: (v) => isNumberIn(v, 0, 1),
  bandColors: (v) => Array.isArray(v) && v.length === DEFAULT_CHART_SETTINGS.bandColors.length && v.every(isHexColor),
};

function parseRange(value: unknown): SavedRange | null {
  if (!isObject(value)) return null;
  if (value.kind === "max") return { kind: "max" };
  if (value.kind === "years" && (value.years === 1 || value.years === 3 || value.years === 5)) {
    return { kind: "years", years: value.years };
  }
  return null;
}

function parseSettings(value: unknown): Partial<ChartSettings> | null {
  if (!isObject(value)) return null;
  const settings: Record<string, unknown> = {};
  for (const [key, setting] of Object.entries(value)) {
    // Only the settings' own checks: "constructor" or "toString" would find
    // the object's built-ins and slip through.
    if (!Object.prototype.hasOwnProperty.call(SETTING_CHECKS, key)) return null;
    if (!SETTING_CHECKS[key as keyof ChartSettings](setting)) return null;
    settings[key] = setting;
  }
  return settings as Partial<ChartSettings>;
}

const isDisplayId = (value: unknown): value is string =>
  typeof value === "string" && !!value.trim() && value.length <= DISPLAY_ID_MAX_LENGTH;

function parseStyle(value: Record<string, unknown>): SavedSeriesStyle | null {
  const { variant, line_style } = value;
  if (variant !== undefined && !isTrendVariant(variant)) return null;
  if (line_style !== undefined && !isLineStyle(line_style)) return null;
  return { ...(variant ? { variant } : {}), ...(line_style ? { line_style } : {}) };
}

/**
 * A series in a chart, with any variant and line style an older config kept
 * on it (they're in `series` now).
 */
function parseSeries(value: unknown): (SavedSeries & { style: SavedSeriesStyle }) | null {
  if (!isObject(value)) return null;
  const { display_id, hidden } = value;
  if (!isDisplayId(display_id)) return null;
  if (hidden !== undefined && typeof hidden !== "boolean") return null;
  const style = parseStyle(value);
  if (!style) return null;
  return { display_id, ...(hidden ? { hidden: true } : {}), style };
}

type ParsedChart = Omit<SavedChart, "id" | "series"> & {
  id?: string;
  series: NonNullable<ReturnType<typeof parseSeries>>[];
};

/** A chart, with its id when it has a valid one (an id-less chart is given one later). */
function parseChart(value: unknown): ParsedChart | null {
  if (!isObject(value) || !Array.isArray(value.series) || value.series.length === 0) return null;
  if (value.id !== undefined && (typeof value.id !== "string" || !CHART_ID.test(value.id))) return null;
  if (value.scale !== null && value.scale !== undefined && !isTrendScale(value.scale)) return null;
  const range = value.range === undefined ? undefined : parseRange(value.range);
  if (range === null) return null;
  const name = value.name === undefined ? undefined : parseName(value.name, CHART_NAME_MAX_LENGTH);
  if (name === null) return null;
  const series = value.series.map(parseSeries);
  if (series.some((s) => s === null)) return null;
  const ids = (series as ParsedChart["series"]).map((s) => s.display_id);
  if (new Set(ids).size !== ids.length) return null;
  return {
    ...(value.id !== undefined ? { id: value.id as string } : {}),
    ...(name ? { name } : {}),
    scale: (value.scale as TrendScale | null | undefined) ?? null,
    ...(range ? { range } : {}),
    series: series as ParsedChart["series"],
  };
}

/**
 * The series' variants and line styles, kept only for series the charts hold.
 * An older config kept them on each chart's series, where a series' first
 * appearance decides; `series` wins over those.
 */
function parseStyles(value: unknown, charts: ParsedChart[]): Record<string, SavedSeriesStyle> | null {
  if (value !== undefined && !isObject(value)) return null;
  const onPage = new Set(charts.flatMap((c) => c.series.map((s) => s.display_id)));
  const styles = new Map<string, SavedSeriesStyle>();
  for (const s of charts.flatMap((c) => c.series)) {
    if (!styles.has(s.display_id) && Object.keys(s.style).length > 0) styles.set(s.display_id, s.style);
  }
  for (const [displayId, entry] of Object.entries(value ?? {})) {
    if (!isDisplayId(displayId) || !isObject(entry)) return null;
    if (Object.keys(entry).some((key) => key !== "variant" && key !== "line_style")) return null;
    const style = parseStyle(entry);
    if (!style) return null;
    if (onPage.has(displayId)) styles.set(displayId, style);
  }
  const kept = Array.from(styles).filter(([, style]) => Object.keys(style).length > 0);
  return Object.fromEntries(kept);
}

/**
 * A saved trend's `config`, checked and with anything unknown dropped, or null
 * when it isn't one: the wrong shape, an unknown variant, scale or line style,
 * no charts, more charts than the page holds, two charts with one id, or more
 * series than can be active at once. A chart saved without an id is given the
 * next free one.
 */
export function parseTrendConfig(value: unknown): TrendConfig | null {
  if (!isObject(value) || value.version !== 1 || !isTrendScale(value.scale)) return null;
  const range = parseRange(value.range);
  if (!range) return null;
  const settings = value.chart_settings === undefined ? {} : parseSettings(value.chart_settings);
  if (!settings) return null;
  if (!Array.isArray(value.charts) || value.charts.length === 0 || value.charts.length > MAX_CHARTS) return null;
  const parsed = value.charts.map(parseChart);
  if (parsed.some((c) => c === null)) return null;
  const styles = parseStyles(value.series, parsed as ParsedChart[]);
  if (!styles) return null;
  const given = (parsed as ParsedChart[]).flatMap((c) => (c.id ? [c.id] : []));
  if (new Set(given).size !== given.length) return null;
  const savedNext = value.next_chart_id;
  if (
    savedNext !== undefined &&
    !(Number.isInteger(savedNext) && (savedNext as number) >= 1 && (savedNext as number) <= MAX_CHART_NUMBER)
  ) {
    return null;
  }
  let next = nextChartNumber(given, savedNext as number | undefined);
  if (next > MAX_CHART_NUMBER) return null;
  const charts: SavedChart[] = (parsed as ParsedChart[]).map(({ id, series, ...c }) => ({
    id: id ?? `t${next++}`,
    ...c,
    series: series.map((s) => ({ display_id: s.display_id, ...(s.hidden ? { hidden: true } : {}) })),
  }));
  const seriesByChart = charts.map((c) => ({ id: c.id, seriesIds: c.series.map((s) => s.display_id) }));
  if (activeSeriesIds(seriesByChart).length > MAX_ACTIVE_SERIES) return null;
  return {
    version: 1,
    scale: value.scale,
    range,
    ...(Object.keys(settings).length > 0 ? { chart_settings: settings } : {}),
    ...(Object.keys(styles).length > 0 ? { series: styles } : {}),
    charts,
    next_chart_id: next,
    ...parseExpanded(value, charts),
  };
}

/**
 * A saved trend's config with a series added as its own chart at the end,
 * the way picking a search result on an open trend adds it. When it can't go
 * on, why: it's there already, or the trend is at its chart or series limit.
 */
export function addSeriesAsNewChart(
  config: TrendConfig,
  displayId: string,
): { config: TrendConfig } | { blocked: string } {
  const onTrend = new Set(config.charts.flatMap((chart) => chart.series.map((s) => s.display_id)));
  if (onTrend.has(displayId)) return { blocked: "Already on this trend" };
  if (config.charts.length >= MAX_CHARTS) return { blocked: `${MAX_CHARTS} charts max` };
  if (onTrend.size >= MAX_ACTIVE_SERIES) return { blocked: `${MAX_ACTIVE_SERIES} series max` };

  const number = nextChartNumber(
    config.charts.map((chart) => chart.id),
    config.next_chart_id,
  );
  return {
    config: {
      ...config,
      charts: [...config.charts, { id: `t${number}`, scale: null, series: [{ display_id: displayId }] }],
      next_chart_id: number + 1,
    },
  };
}

/** How many charts a saved config holds, and how many different series across them. */
export function configCounts(config: unknown): { chart_count: number; series_count: number } {
  const charts = isObject(config) && Array.isArray(config.charts) ? config.charts : [];
  const ids = new Set(
    charts.flatMap((c) =>
      isObject(c) && Array.isArray(c.series)
        ? c.series.flatMap((s) => (isObject(s) && typeof s.display_id === "string" ? [s.display_id] : []))
        : [],
    ),
  );
  return { chart_count: charts.length, series_count: ids.size };
}

/** A saved trend's row as the list shows it: its name, when it changed, and how many charts and series it has. */
export function toSavedTrendSummary<T extends { config: unknown }>({ config, ...row }: T) {
  return { ...row, ...configCounts(config) };
}

/**
 * The enlarged charts, each once and only ones the config has. Only layout,
 * so anything else is dropped rather than refusing the trend. Reads the older
 * single `expanded_chart_id` too.
 */
function parseExpanded(value: Record<string, unknown>, charts: SavedChart[]): Pick<TrendConfig, "expanded_chart_ids"> {
  const listed = Array.isArray(value.expanded_chart_ids)
    ? value.expanded_chart_ids
    : typeof value.expanded_chart_id === "string"
      ? [value.expanded_chart_id]
      : [];
  const ids = new Set(charts.map((c) => c.id));
  const expanded = Array.from(new Set(listed.filter((id): id is string => typeof id === "string" && ids.has(id))));
  return expanded.length > 0 ? { expanded_chart_ids: expanded } : {};
}

/** A name with its outer spaces trimmed, or null when it's empty or too long. */
function parseName(value: unknown, maxLength: number): string | null {
  if (typeof value !== "string") return null;
  const name = value.trim();
  return name.length > 0 && name.length <= maxLength ? name : null;
}

/** A trend name with its outer spaces trimmed, or null when it's empty or too long. */
export function parseTrendName(value: unknown): string | null {
  return parseName(value, TREND_NAME_MAX_LENGTH);
}
